import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, delay, Observable, of, throwError } from 'rxjs';
import {
  keepsFreeText,
  requiresFreeText,
  takesReturnReason,
} from '../enums/expense-status.enum';
import { ExpenseDto } from '../models/expense-api.model';
import {
  CreateExpensePayload,
  Expense,
  ExpenseDriverOption,
  ExpenseRef,
  ExpenseSortField,
  ExpenseStatusChangePayload,
  ExpensesPage,
  ExpensesQuery,
  SortDirection,
  UpdateExpensePayload,
} from '../models/expense.model';
import { ExpensesDataSource } from './expenses.data-source';
import { MOCK_DRIVERS, MOCK_EXPENSES } from './expenses.mock-data';
import { centsToApiAmount, toDriverOption, toExpense } from './expenses.mapper';

/** Latencia simulada para que los estados de carga sean visibles y testeables. */
const SIMULATED_LATENCY_MS = 350;

/**
 * Implementación en memoria de `ExpensesDataSource`.
 *
 * Reproduce el contrato del servidor de forma literal —filtra, ordena y pagina
 * en «servidor», y rechaza con 404 los gastos sin `ExpenseId`— para que el
 * contenedor sea exactamente el mismo con la API real.
 */
@Injectable()
export class ExpensesMockDataSource extends ExpensesDataSource {
  private expenses: ExpenseDto[] = MOCK_EXPENSES.map((expense) => ({
    ...expense,
  }));

  private sequence = this.expenses.length;

  /** Comprobantes «subidos» en esta sesión, como data URL. */
  private readonly uploads = new Map<File, string>();

  list(query: ExpensesQuery): Observable<ExpensesPage> {
    return defer(() => {
      const matching = this.applyFiltersAndSort(query);
      const start = (query.page - 1) * query.pageSize;
      const items = matching
        .slice(start, start + query.pageSize)
        .map(toExpense);

      return of<ExpensesPage>({ items, totalCount: matching.length });
    }).pipe(delay(SIMULATED_LATENCY_MS));
  }

  listAll(query: ExpensesQuery): Observable<Expense[]> {
    return defer(() => of(this.applyFiltersAndSort(query).map(toExpense))).pipe(
      delay(SIMULATED_LATENCY_MS)
    );
  }

  getOne(ref: ExpenseRef): Observable<Expense> {
    return defer(() => {
      const found = this.find(ref);

      return found
        ? of(toExpense(found))
        : throwError(() => this.notFound(ref));
    }).pipe(delay(SIMULATED_LATENCY_MS));
  }

  create(payload: CreateExpensePayload): Observable<Expense> {
    return defer(() => {
      if (requiresFreeText(payload.status) && !payload.reason) {
        return throwError(() => this.reasonRequired());
      }

      this.sequence += 1;

      const driver = MOCK_DRIVERS.find(
        (candidate) => candidate.driverId === payload.driverId
      );

      const created: ExpenseDto = {
        ExpenseId: `${String(this.sequence).padStart(
          8,
          '0'
        )}-0000-4000-8000-000000000001`,
        NodeId: payload.nodeId,
        // El backend lo deduce del nodo; aquí se recorta el sufijo del índice.
        OrderHash: payload.nodeId.replace(/-\d+$/, ''),
        ZoneId: null,
        ReportedAt: this.nowAsLocalIso(),
        DriverId: payload.driverId,
        DriverName: payload.driverName ?? driver?.fullName ?? null,
        LicensePlate: payload.licensePlate,
        Category: payload.category,
        Description: payload.description,
        // Ida y vuelta por el formato del multipart, para ejercitar la conversión.
        Amount: Number(centsToApiAmount(payload.amountInCents)),
        Status: payload.status,
        RejectionReason: keepsFreeText(payload.status) ? payload.reason : null,
        ReceiptUrl: this.receiptUrlFor(payload.receiptFile),
      };

      this.expenses = [created, ...this.expenses];

      return of(toExpense(created));
    }).pipe(delay(SIMULATED_LATENCY_MS));
  }

  update(
    ref: ExpenseRef,
    payload: UpdateExpensePayload
  ): Observable<Expense> {
    return defer(() => {
      const current = this.find(ref);

      if (!current) {
        return throwError(() => this.notFound(ref));
      }

      if (requiresFreeText(payload.status) && !payload.reason) {
        return throwError(() => this.reasonRequired());
      }

      const updated: ExpenseDto = {
        ...current,
        Category: payload.category,
        Description: payload.description,
        Amount: Number(centsToApiAmount(payload.amountInCents)),
        Status: payload.status,
        RejectionReason: keepsFreeText(payload.status) ? payload.reason : null,
        // Tres casos del contrato: fichero nuevo sustituye, nada conserva y
        // `removeReceipt` deja el gasto sin comprobante.
        ReceiptUrl: payload.receiptFile
          ? this.receiptUrlFor(payload.receiptFile)
          : payload.removeReceipt
          ? null
          : current.ReceiptUrl,
      };

      this.replace(updated);

      return of(toExpense(updated));
    }).pipe(delay(SIMULATED_LATENCY_MS));
  }

  changeStatus(
    ref: ExpenseRef,
    payload: ExpenseStatusChangePayload
  ): Observable<void> {
    return defer(() => {
      const current = this.find(ref);

      if (!current) {
        return throwError(() => this.notFound(ref));
      }

      if (requiresFreeText(payload.status) && !payload.reason) {
        return throwError(() => this.reasonRequired());
      }

      this.replace({
        ...current,
        Status: payload.status,
        RejectionReason: keepsFreeText(payload.status) ? payload.reason : null,
        ReturnReasonCode: takesReturnReason(payload.status)
          ? payload.reasonCode
          : null,
      });

      // El endpoint real responde 204 sin cuerpo.
      return of(undefined);
    }).pipe(delay(SIMULATED_LATENCY_MS));
  }

  remove(ref: ExpenseRef): Observable<void> {
    return defer(() => {
      const current = this.find(ref);

      if (!current) {
        return throwError(() => this.notFound(ref));
      }

      this.expenses = this.expenses.filter(
        (expense) =>
          !(
            expense.NodeId === ref.nodeId && expense.ExpenseId === ref.expenseId
          )
      );

      // El endpoint real responde 204 sin cuerpo.
      return of(undefined);
    }).pipe(delay(SIMULATED_LATENCY_MS));
  }

  searchDrivers(searchTerm?: string): Observable<ExpenseDriverOption[]> {
    return defer(() => {
      const term = searchTerm?.trim().toLowerCase() ?? '';
      const matching = term
        ? MOCK_DRIVERS.filter((driver) =>
            driver.fullName.toLowerCase().includes(term)
          )
        : MOCK_DRIVERS;

      return of(matching.map(toDriverOption));
    }).pipe(delay(SIMULATED_LATENCY_MS));
  }

  // ── Simulación del servidor ────────────────────────────────────────────────

  private applyFiltersAndSort(query: ExpensesQuery): ExpenseDto[] {
    const term = query.searchTerm.trim().toLowerCase();

    const filtered = this.expenses.filter((expense) => {
      const matchesTerm =
        !term ||
        expense.OrderHash.toLowerCase().includes(term) ||
        (expense.LicensePlate ?? '').toLowerCase().includes(term) ||
        (expense.DriverName ?? '').toLowerCase().includes(term);

      const matchesCategory =
        query.categories.length === 0 ||
        query.categories.includes(expense.Category);

      // Filtrar por estado deja fuera el histórico, que no tiene estado:
      // mismo comportamiento que el backend.
      const matchesStatus =
        query.statuses.length === 0 ||
        (expense.Status !== null && query.statuses.includes(expense.Status));

      const matchesZone =
        query.zoneIds.length === 0 ||
        (expense.ZoneId !== null && query.zoneIds.includes(expense.ZoneId));

      // Un gasto cuyo pedido no resuelve cliente se cae al filtrar por cliente.
      const matchesClient =
        query.clientIds.length === 0 ||
        (!!expense.ClientId && query.clientIds.includes(expense.ClientId));

      /*
       * Rango de fechas con los dos extremos incluidos. Se compara el `YYYY-MM-DD`
       * de la cadena, sin construir `Date`, para no desplazar el día por zona
       * horaria. Y, como con el estado, un rango deja fuera el histórico: esos
       * gastos aún no tienen fecha.
       */
      const day = expense.ReportedAt?.slice(0, 10) ?? null;
      const matchesDates =
        (!query.fromDate && !query.toDate) ||
        (day !== null &&
          (!query.fromDate || day >= query.fromDate) &&
          (!query.toDate || day <= query.toDate));

      return (
        matchesTerm &&
        matchesCategory &&
        matchesStatus &&
        matchesZone &&
        matchesClient &&
        matchesDates
      );
    });

    return this.sort(filtered, query.sort.field, query.sort.direction);
  }

  private sort(
    expenses: ExpenseDto[],
    field: ExpenseSortField,
    direction: SortDirection
  ): ExpenseDto[] {
    const factor = direction === 'asc' ? 1 : -1;

    return [...expenses].sort(
      (left, right) => factor * this.compare(left, right, field)
    );
  }

  private compare(
    left: ExpenseDto,
    right: ExpenseDto,
    field: ExpenseSortField
  ): number {
    switch (field) {
      case 'reportedAt':
        return this.compareText(left.ReportedAt, right.ReportedAt);
      case 'amount':
        return left.Amount - right.Amount;
      case 'status':
        return (left.Status ?? -1) - (right.Status ?? -1);
      case 'category':
        return this.compareText(left.Category, right.Category);
      case 'orderHash':
        return this.compareText(left.OrderHash, right.OrderHash);
      case 'driverName':
        return this.compareText(left.DriverName, right.DriverName);
      case 'licensePlate':
        return this.compareText(left.LicensePlate, right.LicensePlate);
    }
  }

  /** Los nulos van al final, como es habitual en un listado ordenable. */
  private compareText(left: string | null, right: string | null): number {
    if (!left && !right) {
      return 0;
    }
    if (!left) {
      return -1;
    }
    if (!right) {
      return 1;
    }

    return left.localeCompare(right, 'es');
  }

  private find(ref: ExpenseRef): ExpenseDto | undefined {
    return this.expenses.find(
      (expense) =>
        expense.NodeId === ref.nodeId && expense.ExpenseId === ref.expenseId
    );
  }

  private replace(expense: ExpenseDto): void {
    this.expenses = this.expenses.map((candidate) =>
      candidate.NodeId === expense.NodeId &&
      candidate.ExpenseId === expense.ExpenseId
        ? expense
        : candidate
    );
  }

  private receiptUrlFor(file: File | null): string | null {
    if (!file) {
      return null;
    }

    const existing = this.uploads.get(file);
    if (existing) {
      return existing;
    }

    const url = URL.createObjectURL(file);
    this.uploads.set(file, url);

    return url;
  }

  /** Fecha local sin offset, igual que la entrega el backend. */
  private nowAsLocalIso(): string {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');

    return (
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
      `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    );
  }

  /*
   * Los errores imitan el contrato: código HTTP y cuerpo `{ "Error": "..." }`,
   * que es de donde el front saca el mensaje. Así el mock sirve de especificación
   * y la pantalla se comporta igual con datos simulados que contra el servidor.
   */
  private notFound(ref: ExpenseRef): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 404,
      statusText: `Expense ${ref.nodeId}/${ref.expenseId}`,
      error: { Error: 'NotFound' },
    });
  }

  private reasonRequired(): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 400,
      error: { Error: 'ReasonRequired' },
    });
  }
}
